/**
 * Calculates the total width of all children elements.
 *
 * @param {Array<HTMLElement>} children - An array of child elements whose widths will be
 * summed.
 *
 * @returns {number} The total width of all child elements.
 */
export function sumWidthOfChildren(children) {
  return children?.reduce((width, { offsetWidth = 0 }) => width + offsetWidth, 0);
}

/**
 * Calculates the total width of children elements including gaps between them.
 *
 * @param {Array<HTMLElement>} children - An array of child elements whose widths will be
 * summed.
 * @param {number} gapWidth - The width of the gap between slides.
 *
 * @returns {number} The total width of all child elements plus gaps.
 */
export function sumWidthOfChildrenWithGaps(children, gapWidth = 12) {
  if (!children || children.length === 0) return 0;
  const totalSlideWidth = sumWidthOfChildren(children);
  const totalGapsWidth = (children.length - 1) * gapWidth;
  return totalSlideWidth + totalGapsWidth;
}

/**
 * Returns the children of the track that are either fully or partially visible
 * within the container.
 *
 * @param {HTMLElement} container - The container element that has the overflow hidden.
 * @param {HTMLElement} track - The track element that contains the slides.
 * @param {boolean} partialVisibility - If true, include children that are partially visible;
 *                                       if false, include only fully visible children.
 * @returns {Array<HTMLElement>} An array of child elements that are visible within
 * the container.
 */
export function getVisibleChildren(container, track, partialVisibility = true) {
  if (!container) return [];
  if (!track) return [];

  const containerRect = container.getBoundingClientRect();

  return [...track.children].reduce((acc, child) => {
    const childRect = child.getBoundingClientRect();

    const { left, right } = childRect;

    if (partialVisibility) {
      const isPartiallyVisible = (
        left < containerRect.right && right > containerRect.left
      );

      if (isPartiallyVisible) acc.push(child);

      return acc;
    }

    const isFullyVisible = (left >= containerRect.left && right <= containerRect.right);
    if (isFullyVisible) acc.push(child);

    return acc;
  }, []);
}
